import React from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '@/modules/landing';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-zapt-navy relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: 'url(https://zapt-cdn.com/dc408fb4-e206-4be6-8677-dfc0f17d99f7/background_image.jpg)' }}
      />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-zapt-navy/80 via-zapt-navy/90 to-zapt-navy/80" />

      {/* Navigation */}
      <nav className="border-b border-white/10 bg-zapt-navy/95 backdrop-blur-md relative z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://zapt-cdn.com/dc408fb4-e206-4be6-8677-dfc0f17d99f7/Zapt_White%2BLogo%2B(1).png" 
                alt="ZAPT" 
                className="h-10 w-auto"
              />
            </Link>
            <Link to="/" className="text-gray-400 hover:text-white transition-colors font-medium">
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 relative z-10">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-10 shadow-xl">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">Privacy Policy</h1>
          <p className="text-gray-400 mb-8">Last updated: October 16, 2025</p>

          <div className="prose prose-invert prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">1. Introduction</h2>
              <p className="text-gray-300 mb-4">
                Welcome to ZAPT ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information when you use our service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-gray-200 mb-3">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li><strong className="text-white">Account Information:</strong> Email address when you sign up or sign in</li>
                <li><strong className="text-white">Repository Data:</strong> GitHub repository information you choose to connect</li>
                <li><strong className="text-white">Questions and Interactions:</strong> Questions you ask and interactions with our AI assistant</li>
                <li><strong className="text-white">Payment Information:</strong> Billing details processed through Stripe (we do not store credit card numbers)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li><strong className="text-white">Usage Data:</strong> Pages visited, features used, time spent on our service</li>
                <li><strong className="text-white">Device Information:</strong> Browser type, IP address, operating system</li>
                <li><strong className="text-white">Analytics Data:</strong> Collected via PostHog and Vercel Analytics</li>
                <li><strong className="text-white">Error Logs:</strong> Technical information when errors occur, collected via Sentry</li>
                <li><strong className="text-white">Cookies:</strong> Small data files stored on your device for authentication and functionality</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">2.3 Code Repository Data</h3>
              <p className="text-gray-300 mb-4">
                When you connect a GitHub repository, we access and analyze your code to provide AI-powered answers. We read your codebase to understand its structure and functionality but do not permanently store your entire codebase.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-300 mb-3">We use your information to:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li>Provide, maintain, and improve our service</li>
                <li>Process your questions and generate AI responses</li>
                <li>Manage your account and subscription</li>
                <li>Send you technical notices and support messages</li>
                <li>Analyze usage patterns to improve our service</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">4. Third-Party Services</h2>
              <p className="text-gray-300 mb-3">We use the following third-party services that may collect information:</p>
              
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-4">
                <ul className="space-y-3 text-gray-300">
                  <li><strong className="text-white">Stytch:</strong> Authentication and user management</li>
                  <li><strong className="text-white">GitHub:</strong> Repository access and code analysis</li>
                  <li><strong className="text-white">Google Gemini AI:</strong> AI-powered question answering</li>
                  <li><strong className="text-white">Stripe:</strong> Payment processing</li>
                  <li><strong className="text-white">PostHog:</strong> Product analytics</li>
                  <li><strong className="text-white">Vercel Analytics:</strong> Website analytics</li>
                  <li><strong className="text-white">Sentry:</strong> Error tracking and monitoring</li>
                  <li><strong className="text-white">Trigger.dev:</strong> Background task processing</li>
                </ul>
              </div>

              <p className="text-gray-300">
                Each service has its own privacy policy governing how they handle data. We encourage you to review their policies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">5. Data Security</h2>
              <p className="text-gray-300 mb-4">
                We implement appropriate technical and organizational security measures to protect your data, including encryption, access controls, and secure hosting. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">6. Data Retention</h2>
              <p className="text-gray-300 mb-4">
                We retain your personal data only for as long as necessary to provide our service and fulfill the purposes outlined in this policy. When you delete your account, we will delete or anonymize your personal data, except where we are required to retain it for legal purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">7. Your Rights</h2>
              <p className="text-gray-300 mb-3">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
                <li><strong className="text-white">Correction:</strong> Update or correct inaccurate data</li>
                <li><strong className="text-white">Deletion:</strong> Request deletion of your data</li>
                <li><strong className="text-white">Portability:</strong> Receive your data in a structured format</li>
                <li><strong className="text-white">Opt-out:</strong> Opt out of marketing communications</li>
                <li><strong className="text-white">Withdraw Consent:</strong> Withdraw previously given consent</li>
              </ul>
              <p className="text-gray-300">
                To exercise these rights, please contact us at the email address below.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">8. International Data Transfers</h2>
              <p className="text-gray-300 mb-4">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with applicable data protection laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">9. Children's Privacy</h2>
              <p className="text-gray-300 mb-4">
                Our service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">10. Changes to This Policy</h2>
              <p className="text-gray-300 mb-4">
                We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">11. Contact Us</h2>
              <p className="text-gray-300 mb-4">
                If you have questions or concerns about this privacy policy or our data practices, please contact us at:
              </p>
              <div className="bg-zapt-purple/20 border border-zapt-purple/30 rounded-xl p-6">
                <p className="text-white font-medium mb-2">ZAPT</p>
                <p className="text-gray-300">Email: info@zapt.ai</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">12. GDPR Compliance (EU Users)</h2>
              <p className="text-gray-300 mb-4">
                If you are located in the European Economic Area (EEA), we process your personal data based on the following legal grounds:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li><strong className="text-white">Contract:</strong> Processing necessary to provide our service</li>
                <li><strong className="text-white">Consent:</strong> You have given explicit consent</li>
                <li><strong className="text-white">Legitimate Interests:</strong> For improving our service and preventing fraud</li>
                <li><strong className="text-white">Legal Obligation:</strong> To comply with applicable laws</li>
              </ul>
            </section>

            <section className="mb-0">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">13. CCPA Compliance (California Users)</h2>
              <p className="text-gray-300 mb-4">
                If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 text-gray-300 space-y-2">
                <li>Right to know what personal information is collected</li>
                <li>Right to delete personal information</li>
                <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
                <li>Right to non-discrimination for exercising your rights</li>
              </ul>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
